import { ReactNode, useCallback, useState } from 'react';

// material-ui
import { alpha, useTheme } from '@mui/material/styles';
import { AppBar, Toolbar, useMediaQuery, AppBarProps, Box, Tooltip, Typography } from '@mui/material';

// project-imports
import IconButton from 'components/@extended/IconButton';

import { DRAWER_WIDTH, MINI_DRAWER_WIDTH } from 'config';
import useConfig from 'hooks/useConfig';
import { dispatch, RootState, useSelector } from 'store';
import { openDrawer } from 'store/reducers/menu';

// assets
import { HambergerMenu } from 'iconsax-react';
import logoutImage from 'assets/icons/svg/output-onlinepngtools (1).png';
import notificationImage from 'assets/icons/svg/output-onlinepngtools (3).png';

// types
import { MenuOrientation, ThemeMode } from 'types/config';
import { useLogoutMutation } from 'api/auth';
import { useLocation } from 'react-router';
import { openDefaultSuccessSnackbar } from 'store/reducers/snackbar';
import { resetDocumentElements } from 'store/slice/design/documentElementsSlice';
import { setLocalStorageItems } from 'utils/fileUtils';
import ConfirmationDialog from 'components/dialog/ConfirmationDialog';
import { showPageBlocker } from 'store/reducers/pageBlockerSlice';

// ==============================|| MAIN LAYOUT - HEADER ||============================== //

const Header = () => {
  const theme = useTheme();
  const downLG = useMediaQuery(theme.breakpoints.down('lg'));

  const { menuOrientation } = useConfig();
  const { drawerOpen } = useSelector((state) => state.menu);
  const [logout, { isLoading: logoutInProgress }] = useLogoutMutation();
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);

  const decodedAuthToken = useSelector((state: RootState) => state.auth.decodedAuthToken);

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;
  const isFormDirty = useSelector((state: RootState) => state.formslice.isDirty);

  const iconBackColorOpen = theme.palette.mode === ThemeMode.DARK ? 'secondary.200' : 'secondary.200';
  const iconBackColor = theme.palette.mode === ThemeMode.DARK ? 'background.default' : 'secondary.100';
  const location = useLocation();

  const onLogout = useCallback(() => {
    setShowLogoutConfirmation(true);
  }, []);

  const onLogoutConfirm = useCallback(async () => {
    try {
      if (decodedAuthToken) {
        setLocalStorageItems({
          user_id: decodedAuthToken.nameid,
          last_location: location.pathname
        });
      }

      await logout().unwrap();
      const lastCloseTime = localStorage.getItem('browserCloseTime');
      if (lastCloseTime) {
        localStorage.removeItem('browserCloseTime');
      }
      dispatch(resetDocumentElements());
      dispatch(
        openDefaultSuccessSnackbar({
          message: 'Your session is logged out successfully',
          preventAutoDismiss: true,
          hideDuration: 15000
        })
      );
    } catch (e) {}
  }, [dispatch, logout, location.pathname]);

  const checkLogoutProcess = useCallback(() => {
    setShowLogoutConfirmation(false);
    if (isFormDirty) {
      //setForcePageBlock(true);
      dispatch(
        showPageBlocker({
          onConfirm: () => {
            onLogoutConfirm();
          },
          onCancel: () => {}
        })
      );
    } else {
      onLogoutConfirm();
    }
  }, [isFormDirty, onLogoutConfirm]);
  // common header
  const mainHeader: ReactNode = (
    <Toolbar sx={{ px: { xs: 2, sm: 4.5, lg: 8 } }}>
      <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" className="full-width">
        <IconButton
          aria-label="open drawer"
          onClick={() => dispatch(openDrawer(!drawerOpen))}
          edge="start"
          color="secondary"
          variant="light"
          size="large"
          sx={{ color: 'secondary.main', bgcolor: drawerOpen ? iconBackColorOpen : iconBackColor, ml: { xs: 0, lg: -2 }, p: 1 }}
        >
          <HambergerMenu />
        </IconButton>
        <Typography className="titleWelcome user_name_mobile" sx={{ fontWeight: 400 }}>
          Welcome {decodedAuthToken?.unique_name}!
        </Typography>
        <Box display="flex" flexDirection="row" gap="12px">
          <Tooltip placement="top" title={'Notification'}>
            <Box sx={{ cursor: 'pointer' }}>
              <img src={notificationImage} className="notifications-icon" />
            </Box>
          </Tooltip>
          <Tooltip placement="top" title={'Log Out'}>
            <Box sx={{ cursor: 'pointer' }} onClick={onLogout}>
              <img src={logoutImage} className={'icon logoutIcon'} />
            </Box>
          </Tooltip>
        </Box>
      </Box>
    </Toolbar>
  );

  // app-bar params
  const appBar: AppBarProps = {
    position: 'fixed',
    elevation: 0,
    sx: {
      bgcolor: alpha(theme.palette.background.default, 0.8),
      backdropFilter: 'blur(8px)',
      zIndex: 1200,
      width: isHorizontal
        ? '100%'
        : { xs: '100%', lg: drawerOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : `calc(100% - ${MINI_DRAWER_WIDTH}px)` }
    }
  };

  return (
    <>
      {downLG && (
        <>
          <AppBar {...appBar}>{mainHeader}</AppBar>
          <ConfirmationDialog
            open={showLogoutConfirmation}
            onClose={() => setShowLogoutConfirmation(false)}
            onConfirm={checkLogoutProcess}
            isLoading={logoutInProgress}
            message="Logout Confirmation"
            additionalContent={
              <Typography mt={1} mb={3}>
                Are you sure you want to logout?
              </Typography>
            }
            titleIconType="warning"
          />
        </>
      )}
    </>
  );
};

export default Header;
